import React, {ReactNode} from "react";
import {Button} from "primereact/button";
import Exception from "../../../../shared/exception/Exception";
import {Dialog} from "primereact/dialog";
import CertificateInput, {CertificateInputType} from "../../../../shared/components/CertificateInput";
import PortalContext from "../../../PortalContext";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../../shared/dialog/components/AbstractDialog";
import {Password} from "primereact/password";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import SignatureAddCertificateDialogFields from "../../field/signature/SignatureAddCertificateDialogFields";
import {
	CertificateEntry,
	CertificateEntryInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export interface SignatureAddCertificateDialogProperties extends AbstractDialogProperties {
	certificate?: string;
	privateKey?: string;
	privateKeyPassword?: string;
	context: PortalContext;
	submit: (certificate: CertificateEntry, privateKey?: string, privateKeyPassword?: string) => void;
}

export interface SignatureAddCertificateDialogState extends AbstractDialogState {
	certificate?: CertificateEntry;
	privateKey?: string;
	privateKeyPassword?: string;
	fieldErrors: Map<SignatureAddCertificateDialogFields, string>;
}

export class SignatureAddCertificateDialogDefinition
	extends AbstractDialog<SignatureAddCertificateDialogProperties, SignatureAddCertificateDialogState> {
	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<SignatureAddCertificateDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<SignatureAddCertificateDialogFields, string>()
		}

		this.hide = this.hide.bind(this);
		this.submit = this.submit.bind(this);
		this.onChange = this.onChange.bind(this);
		this.renderError = this.renderError.bind(this);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();
		this.props.submit(this.state.certificate!, this.state.privateKey, this.state.privateKeyPassword);
		this.hide();
	}

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.setState(
			{
				certificate: undefined,
				privateKey: undefined
			}
		);

		super.hide();
	}

	/**
	 * Render the dialog header
	 */
	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-certificate small"}/>
					<Translate {...this.props} data="signature.general.signature.certificate.add.title"
							   namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.select")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		let errors: Map<any, string> = this.state.fieldErrors;
		return errors.size === 0 && !this.state.submitted && FunctionHelper.exists(this.state.certificate) &&
			FunctionHelper.applyValue(this.state.privateKey, "") !== "" &&
			FunctionHelper.applyValue(this.state.privateKeyPassword, "") !== "" &&
			!FunctionHelper.exists(this.state.exception);
	}

	protected renderDialog(): ReactNode {
		let errors: Map<SignatureAddCertificateDialogFields, string> = this.state.fieldErrors;
		let certificate: x509.X509Certificate | undefined;

		if (FunctionHelper.exists(this.state.certificate)) {
			try {
				certificate = new x509.X509Certificate(this.state.certificate?.certificate)
			} catch (e: any) {
				// can be ignored here
			}
		}

		let classList: Array<string> = ["signature-add-certificate-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					resizable={false} closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.signature.add"} namespace={"help"} markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<label className="p-col-12 label-left" htmlFor="control-certificate">
								<Translate {...this.props}
										   data="signature.general.signature.certificate.add.certificate"
										   namespace="dialog"
										   values={{
											   certificate: FunctionHelper.exists(this.state.exception) ||
											   errors.has(SignatureAddCertificateDialogFields.CERTIFICATE) ?
												   "" : FunctionHelper.applyValue(certificate?.subject, "")
										   }}/>
							</label>
							<div className="p-col-12 p-grid">
								<CertificateInput {...this.props} inputId="control-certificate"
												  className={
													  errors.has(SignatureAddCertificateDialogFields.CERTIFICATE) ?
														  " p-invalid" : ""
												  }
												  tooltip={
													  errors.has(SignatureAddCertificateDialogFields.CERTIFICATE) ?
														  Translate.value(
															  errors.get(SignatureAddCertificateDialogFields.CERTIFICATE)!,
															  {ns: "message"}
														  ) : ""
												  }
												  type={CertificateInputType.CERTIFICATE}
												  value={FunctionHelper.applyValue(
													  this.state.certificate?.certificate, ""
												  )}
												  onChange={(certificateEntry: CertificateEntry) => this.onChange(
													  certificateEntry, SignatureAddCertificateDialogFields.CERTIFICATE
												  )}/>
								{this.renderFieldError(errors.has(SignatureAddCertificateDialogFields.CERTIFICATE) ?
									Translate.value(
										errors.get(SignatureAddCertificateDialogFields.CERTIFICATE)!,
										{ns: "message"}
									) : ""
								)}
							</div>
						</div>
						<div className="p-col-12 p-grid">
							<label className="p-col-12 label-left" htmlFor="control-key">
								<Translate {...this.props}
										   data="signature.general.signature.certificate.add.private_key"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-grid">
								<CertificateInput{...this.props} inputId="control-key"
												 className={
													 errors.has(SignatureAddCertificateDialogFields.PRIVATE_KEY) ?
														 " p-invalid" : ""
												 }
												 tooltip={
													 errors.has(SignatureAddCertificateDialogFields.PRIVATE_KEY) ?
														 Translate.value(
															 errors.get(SignatureAddCertificateDialogFields.PRIVATE_KEY)!,
															 {ns: "message"}
														 ) : ""
												 }
												 type={CertificateInputType.PRIVATE_KEY}
												 value={FunctionHelper.applyValue(
													 this.state.privateKey, ""
												 )}
												 onChange={(certificateEntry: CertificateEntry) => this.onChange(
													 certificateEntry, SignatureAddCertificateDialogFields.PRIVATE_KEY
												 )}/>
								{this.renderFieldError(errors.has(SignatureAddCertificateDialogFields.PRIVATE_KEY) ?
									Translate.value(
										errors.get(SignatureAddCertificateDialogFields.PRIVATE_KEY)!,
										{ns: "message"}
									) : ""
								)}
							</div>
						</div>
						<div className="p-col-12 p-grid" hidden={FunctionHelper.isEmpty(this.state.privateKey)}>
							<label className="p-col-12 label-left" htmlFor="control-password">
								<Translate {...this.props} data="signature.general.signature.certificate.add.password"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-6 p-grid">
								<Password inputId="control-password" toggleMask={true} feedback={false}
										  value={
											  FunctionHelper.applyValue(this.state.privateKeyPassword, "")
										  }
										  className={
											  errors.has(SignatureAddCertificateDialogFields.PASSWORD) ?
												  "p-invalid" : ""
										  }
										  tooltip={
											  errors.has(SignatureAddCertificateDialogFields.PASSWORD) ?
												  Translate.value(
													  errors.get(SignatureAddCertificateDialogFields.PASSWORD)!,
													  {ns: "message"}
												  ) : ""
										  }
										  onChange={(e) => this.onChange(
											  e.currentTarget.value, SignatureAddCertificateDialogFields.PASSWORD
										  )}/>
								{this.renderFieldError(errors.has(SignatureAddCertificateDialogFields.PASSWORD) ?
									Translate.value(
										errors.get(SignatureAddCertificateDialogFields.PASSWORD)!,
										{ns: "message"}
									) : ""
								)}
							</div>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	private onChange(value: any, field: SignatureAddCertificateDialogFields): void {
		let exception: Exception | undefined;
		let errors: Map<SignatureAddCertificateDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case SignatureAddCertificateDialogFields.CERTIFICATE:
				if (FunctionHelper.isEmpty(value.certificate)) {
					errors.set(field, "errors.fields.set");
				} else {
					try {
						let certificate: x509.X509Certificate = new x509.X509Certificate(value.certificate);
						errors.delete(field);

						if (certificate.notAfter < new Date()) {
							exception = new Exception(Translate.value("errors.fields.certificate.date", {ns: "message"}));
							errors.set(field, "");
						}
					} catch (e: any) {
						exception = new Exception(Translate.value("errors.fields.certificate.invalid", {ns: "message"}));
						errors.set(field, "");
					}
				}

				this.setState({
					certificate: value,
					exception: exception
				});
				break;
			case SignatureAddCertificateDialogFields.PRIVATE_KEY:
				let privateKey: string | undefined;

				if (FunctionHelper.isEmpty(value.certificate)) {
					errors.set(field, "errors.fields.set");
				} else {
					errors.delete(field);
					privateKey = value.certificate;
				}

				this.setState({
					privateKey: privateKey
				});
				break;
			case SignatureAddCertificateDialogFields.PASSWORD:
				let password: string | undefined;

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else {
					errors.delete(field);
					password = value;
				}

				this.setState({
					privateKeyPassword: password
				});
				break;
		}
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		this.setState({
			certificate: CertificateEntry.fromJson({
				certificate: this.props.certificate
			} as CertificateEntryInterface),
			privateKey: this.props.privateKey,
			privateKeyPassword: this.props.privateKeyPassword,
		});
	};
}

const SignatureAddCertificateDialog = React.memo(SignatureAddCertificateDialogDefinition,
	(
		prevProps: Readonly<SignatureAddCertificateDialogProperties>,
		nextProps: Readonly<SignatureAddCertificateDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default SignatureAddCertificateDialog;